import React, { useCallback, useEffect, useState } from 'react'
import cx from 'classnames'
import DOMPurify from 'dompurify'
import { Link } from 'react-router-dom'
import { BtnV2, BtnV2Props, Icon, l, Tabs } from '@manychat/manyui'
import { TunnelPlaceholder } from '@manychat/react-tunnels'

import { useAppDispatch } from 'reduxTyped'
import { DashboardTabType } from 'apps/dashboard/constants/DashboardTabType'
import { BroadcastTabType } from 'apps/posting/pages/BroadcastBuilder/components/SimplifiedBroadCast/constants'
import { setPageTab } from 'common/core/actions/pageActions'
import { toggleSidebar } from 'common/core/actions/uiActions'
import Responsive from 'components/Responsive/Responsive'
import { analyticsService } from 'utils/services/analytics'

import styles from './Title.module.css'

export type TabValue = DashboardTabType | BroadcastTabType
interface Tab {
  value: TabValue
  id: string
  'data-test-id'?: string
  beta?: boolean
  label: string
}

const Title = ({
  onTabChange,
  onHelpClick,
  theme = {},
  responsivePadding,
  hideMenuIcon,
  button,
  help: Help,
  tabs,
  activeTab,
  breadcrumbs,
  search,
  url,
  className,
  title,
  children,
  tools,
}: {
  onTabChange?: (tab: string) => void
  onHelpClick?: () => void
  theme?: {
    main?: string
    wrap?: string
    back?: string
    title?: string
    helpIcon?: string
    tools?: string
  }
  responsivePadding?: boolean
  hideMenuIcon?: boolean
  button?: React.ReactElement | BtnV2Props
  help?: React.ReactElement | React.FunctionComponent<{ open: boolean }> | string
  tabs?: Tab[]
  activeTab?: string
  breadcrumbs?: React.ReactNode
  search?: React.ReactNode | string
  url?: string
  className?: string
  title?: React.ReactNode | string
  children?: React.ReactNode
  tools?: React.ReactNode | React.ReactNode[]
}) => {
  const dispatch = useAppDispatch()
  let helpTimer: ReturnType<typeof setTimeout>
  let hoverEvent: ReturnType<typeof setTimeout>

  const [showHelp, setShowHelp] = useState(false)

  const handleTabChange = useCallback(
    (tabType: string) => {
      dispatch(setPageTab(tabType))
      onTabChange && onTabChange(tabType as TabValue)
    },
    [onTabChange, dispatch],
  )

  const handleHelpMouseEnter = () => {
    clearTimeout(helpTimer)
    helpTimer = setTimeout(() => {
      setShowHelp(true)
      hoverEvent = setTimeout(() => {
        analyticsService.sendEvent('SUPPORT.HELPER_HOVER_TITLE', {
          url: window.location.pathname,
        })
      }, 1500)
    }, 250)
  }

  const handleHelpMouseLeave = () => clearTimeout(hoverEvent)

  const handleMouseLeave = () => {
    if (document.fullscreenElement !== null) {
      return
    }
    clearTimeout(helpTimer)
    helpTimer = setTimeout(() => setShowHelp(false), 250)
  }

  const handleHelpTriggerClick = () => {
    if (onHelpClick) {
      return onHelpClick()
    }
    clearTimeout(helpTimer)
    setShowHelp(!showHelp)
  }

  const handleMenuClick = () => dispatch(toggleSidebar())

  useEffect(() => {
    return () => clearTimeout(helpTimer)
  })

  const wrapperClassName = cx(
    styles.wrap,
    theme.wrap,
    'title-wrapper',
    {
      [styles.responsivePadding]: responsivePadding,
    },
    className,
  )

  return (
    <div className={wrapperClassName} onMouseLeave={handleMouseLeave} data-test-id="cms-flow-title">
      <div className={cx(styles.main, 'title-main', theme.main)}>
        <div className={cx(styles.title, theme.title)}>
          <TunnelPlaceholder id="layout-title-breadcrumbs" />
          {breadcrumbs ? breadcrumbs : null}
          <a className={cx(styles.preview, 'text-heading text-ellipsis d-flex align-center')}>
            <TunnelPlaceholder id="layout-title-title" />
            {title || children}
          </a>
          {search ? search : null}
          {Help && (
            <div
              className={cx(styles.helpTrigger, theme.helpIcon, {
                'text-primary': showHelp,
              })}
              onMouseEnter={handleHelpMouseEnter}
              onClick={handleHelpTriggerClick}
              onMouseLeave={handleHelpMouseLeave}
            >
              <Icon.Question className="pointer" size={24} />
            </div>
          )}
          {Boolean(onHelpClick) && (
            <Icon.Question
              className="pointer text-h-primary m-l-xs"
              onClick={handleHelpTriggerClick}
              size={24}
            />
          )}
          {url && (
            <Link
              to={url}
              className="m-l-md text-md text-secondary title-stats-link d-flex align-end"
            >
              <Icon.Analytics className="m-r-xs" />
              {l.translate('Stats')}
            </Link>
          )}
          {Help && (
            <div
              className={cx(styles.helpWrapper, 'title-help-wrapper', {
                [styles.showHelp]: showHelp,
              })}
              onMouseEnter={handleHelpMouseEnter}
            >
              <div className={cx(styles.help, 'title-help')}>
                <div>
                  {React.isValidElement(Help) ? (
                    Help
                  ) : (
                    <div className={styles.content}>
                      {typeof Help === 'function' ? (
                        <Help open={showHelp} />
                      ) : (
                        <div
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(Help as string) }}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className={cx(styles.center, 'text-center nowrap text-ellipsis')}>
          <TunnelPlaceholder id="layout-title-center" />
        </div>
        <div className={cx(styles.tools, 'title-tools', theme.tools)}>
          <TunnelPlaceholder id="layout-title-tools" />
          {button && (
            <>
              {React.isValidElement(button) ? (
                button
              ) : (
                <BtnV2 variant="primary" size="small" {...(button as BtnV2Props)} />
              )}
            </>
          )}
          {tools && (
            <>{Array.isArray(tools) ? tools.map((tool, i) => <div key={i}>{tool}</div>) : tools}</>
          )}
          {!hideMenuIcon && (
            <Responsive maxWidth={900}>
              <Icon.Menu className={styles.sidebarToggle} onClick={handleMenuClick} size={24} />
            </Responsive>
          )}
        </div>
      </div>
      {tabs && (
        <div className={cx(styles.tabs, 'tabs-wrapper')}>
          <Tabs options={tabs} value={activeTab} onChange={handleTabChange} />
        </div>
      )}
    </div>
  )
}

export default Title
